// A workflow edge's branch condition: the `if` / `else` a connection doc carries so the trigger
// runner can decide whether to follow that arrow. Pure and Angular-free, same rule as link-kind.ts /
// step-payload.ts / msg-connector.ts, so the build/parse round-trip is unit-tested on plain objects
// without standing the canvas up.
//
// The stored shape is the backend contract (see the workflow-builder frontend contract): a connection
// is `{ source, target }` plus at most one of
//   - `if`:   a JSON-logic rule, followed when it evaluates true;
//   - `else`: true, the fallback arrow, followed when no sibling `if` from the same source matched.
// JSON-logic is a tree of `{ operator: [args] }` the runner evaluates safely (no code execution).
//
// This module only builds and reads back the SIMPLE single-comparison rules the click-an-arrow editor
// produces (`output.category == "refund"`). A hand-written `and`/`or` rule still stores and runs; the
// editor just cannot render it, so parseIfRule returns null for it and the editor says so.

// One comparison the editor can show: which field, which check, and the value to compare against.
export interface EdgeComparison {
  field: string;
  operator: string;
  value: string;
}

// A check offered in the operator dropdown. `short` is the compact form drawn on the arrow label;
// `takesValue` is false only for `exists`, which asks whether a field is set at all.
export interface WorkflowOperator {
  value: string;
  label: string;
  short: string;
  takesValue: boolean;
}

// The checks the editor offers, in the order they appear. Equality and contains first because they
// are what most branches test; the numeric comparisons and the string prefix/suffix follow.
export const WORKFLOW_OPERATORS: WorkflowOperator[] = [
  { value: 'eq',       label: 'equals',           short: '=',        takesValue: true },
  { value: 'ne',       label: 'does not equal',   short: '≠',   takesValue: true },
  { value: 'contains', label: 'contains',         short: 'contains', takesValue: true },
  { value: 'gt',       label: 'is greater than',  short: '>',        takesValue: true },
  { value: 'gte',      label: 'is at least',      short: '≥',   takesValue: true },
  { value: 'lt',       label: 'is less than',     short: '<',        takesValue: true },
  { value: 'lte',      label: 'is at most',       short: '≤',   takesValue: true },
  { value: 'starts',   label: 'starts with',      short: 'starts',   takesValue: true },
  { value: 'ends',     label: 'ends with',        short: 'ends',     takesValue: true },
  // MEMBERSHIP, and deliberately not the same question as `contains`. `contains` asks whether a typed
  // text sits INSIDE the field (the field is the haystack); this asks whether the FIELD is one of several
  // accepted values (the list is the haystack). Both encode to `in`, on opposite sides - see
  // buildComparison. Without this row the only word that reads like a list check was `contains`, so a
  // typed "a@x.com, b@y.com" became one 56-character needle that matched no single address.
  { value: 'oneof',    label: 'is one of',        short: 'one of',   takesValue: true },
  { value: 'exists',   label: 'is set',           short: 'is set',   takesValue: false },
  // The exact complement of `exists`, and the only way to author the contract's own "not opted out"
  // example ({ '!': [{ var: 'item.optedOut' }] }). Reads a field's ABSENCE, so it takes no value either.
  { value: 'empty',    label: 'is not set',       short: 'not set',  takesValue: false }
];

// Whether the chosen operator needs a value box. `exists` reads a field's presence, so it takes none;
// everything else compares against something. Unknown operators default to taking a value.
export function operatorTakesValue(operator: string): boolean {
  return WORKFLOW_OPERATORS.find(o => o.value === operator)?.takesValue !== false;
}

// The numeric comparisons, kept in one place so build (coercion) and validate (must be a number)
// cannot disagree about which operators are numeric.
const NUMERIC_OPERATORS = ['gt', 'gte', 'lt', 'lte'];
// The text operators, where the value stays a raw string (a number typed here is matched as text).
const TEXT_OPERATORS = ['contains', 'starts', 'ends'];
// The operators whose ONE value box holds several values, typed comma-separated. A list rather than a
// single id so a second membership check ("is none of") drops in without touching each branch below.
const LIST_OPERATORS = ['oneof'];

// Whether this operator's value is a comma-separated LIST rather than one scalar. Exported so the editor
// can drop the compare-against-a-field control and workflow-filter can validate the entries without
// either of them repeating the operator id.
export function operatorTakesList(operator: string): boolean {
  return LIST_OPERATORS.includes(operator);
}

// A typed "a@x.com, b@y.com" -> its entries, trimmed, with blanks dropped. Split on the COMMA only, never
// on whitespace, so a value that legitimately contains a space survives intact.
export function splitListValue(raw: string): string[] {
  return (raw ?? '').toString().split(',').map(s => s.trim()).filter(s => s !== '');
}

// The entries back as the single string the value box holds - the inverse of splitListValue for anything
// it produced, so a stored list reopens as the text that built it.
function joinListValue(values: any[]): string {
  return (values || []).map(v => asText(v)).join(', ');
}

// Told wherever a list is typed, because the COMMA is the thing that makes it a list. Shared so the gate
// builder and the repeat's filter cannot word it differently.
export const LIST_VALUE_NOTE = 'Separate the values with commas.';

// The value as the rule should carry it. A LIST operator yields an array; text operators keep the raw
// string; everything else is one numeric-aware scalar.
function coerceValue(raw: string, operator: string): any {
  // Split FIRST, then coerce each entry by the same scalar rule, so a list of ids compares numerically
  // exactly as `equals` would - the two must not disagree about what "8" means.
  if (operatorTakesList(operator)) { return splitListValue(raw).map(entry => coerceScalar(entry)); }
  if (TEXT_OPERATORS.includes(operator)) { return raw ?? ''; }
  return coerceScalar(raw);
}

// One value's stored form: a number-looking value becomes a number (so `>= 8` compares numerically, and
// `== 8` matches the runner's numeric awareness), and true/false become booleans.
function coerceScalar(raw: string): any {
  const trimmed = (raw ?? '').toString().trim();
  if (trimmed === '') { return raw ?? ''; }
  if (!isNaN(Number(trimmed))) { return Number(trimmed); }
  if (trimmed === 'true') { return true; }
  if (trimmed === 'false') { return false; }
  return raw;
}

// A single comparison -> the JSON-logic rule stored under the edge's `if`. The arg order is the
// contract's: `in` takes the needle first and the field second (substring / membership), the string
// prefix/suffix take the field first, and every other operator takes the field first.
export function buildIfRule(field: string, operator: string, value: string): any {
  return buildComparison(field, operator, coerceValue(value, operator));
}

// The same encoder with the right-hand side already built, so a comparison against ANOTHER field
// ({ var: 'output.threshold' }) uses the identical operator table and arg order as one against a
// literal. EXPORTED because workflow-filter.ts builds exactly these rules for a repeat's per-entry
// `where`, and a second copy of the table there would let the two drift over an arg order that is only
// wrong at run time.
export function buildComparison(field: string, operator: string, rhs: any): any {
  const varRef = { var: (field || '').trim() };
  switch (operator) {
    case 'eq':       return { '==': [varRef, rhs] };
    case 'ne':       return { '!=': [varRef, rhs] };
    case 'gt':       return { '>':  [varRef, rhs] };
    case 'gte':      return { '>=': [varRef, rhs] };
    case 'lt':       return { '<':  [varRef, rhs] };
    case 'lte':      return { '<=': [varRef, rhs] };
    case 'contains': return { 'in': [rhs, varRef] };
    // NOT { in: [varRef, rhs] }, the obvious mirror of contains: rhs is a LIST there, so that rule holds
    // an array inside an array - see orOfEquals for why that cannot be stored at all.
    case 'oneof':    return orOfEquals(varRef, rhs);
    case 'starts':   return { 'starts_with': [varRef, rhs] };
    case 'ends':     return { 'ends_with': [varRef, rhs] };
    case 'exists':   return { '!!': [varRef] };
    case 'empty':    return { '!':  [varRef] };
    default:         return { '==': [varRef, rhs] };
  }
}

// A membership check as an OR of equalities, rather than as one `in` against a list. The reason is
// STORAGE, not logic: these rules live in a Firestore document, and a Firestore array may hold strings,
// numbers and maps but never another array - so { in: [field, ['a','b']] } is refused by the SDK before
// it is sent. That refusal carries a `code` and no `status`, so the pane's error mapper reported it as
// "No connection. Check your network and try again." and the check looked like a network fault forever.
//
// This shape puts only maps inside the array, means exactly the same thing, and uses the same `or` the
// filter already emits for its own join - so it asks nothing new of the runner.
//
// ONE entry stores the bare `==`, so it reopens as `equals`: the same check under the name that
// describes it. An EMPTY list stores { or: [] }, which matches nothing exactly as the old shape did -
// both validators refuse it before a save, so that is a floor and not a path.
function orOfEquals(varRef: any, values: any): any {
  const list = Array.isArray(values) ? values : [values];
  const clauses = list.map(value => ({ '==': [varRef, value] }));
  return clauses.length === 1 ? clauses[0] : { or: clauses };
}

// The value as the rule should carry it, for callers that build a comparison themselves. Exported for
// workflow-filter.ts, so a filter's literal is coerced by the same rule the gate uses - a number typed
// against `is at least` becomes a number in both, and a text operator keeps its raw string in both.
export { coerceValue as coerceRuleValue };

// The JSON-logic operators the editor understands, mapped back to its own operator ids. The strict
// pair (`===` / `!==`) folds onto the loose one: the editor offers only loose equality, so a strict
// rule reopens as loose and a re-save writes loose - a deliberate, documented narrowing.
const JSONLOGIC_TO_OPERATOR: Record<string, string> = {
  '==': 'eq', '===': 'eq', '!=': 'ne', '!==': 'ne',
  '>': 'gt', '>=': 'gte', '<': 'lt', '<=': 'lte',
  'starts_with': 'starts', 'ends_with': 'ends'
};

// The dotted path inside a `{ var: ... }` node, or null when the node is not a var reference. The
// array form `{ var: [path, default] }` reports its path; the default is dropped (the editor has no
// field for it, and re-saving simply omits it). Exported because workflow-loop.ts reads the same node
// out of a repeat's `items` rule - one reader for the shape, never two that can drift.
export function varPath(node: any): string | null {
  if (!node || typeof node !== 'object' || Array.isArray(node)) { return null; }
  if (!('var' in node)) { return null; }
  const raw = Array.isArray(node.var) ? node.var[0] : node.var;
  return raw === null || raw === undefined ? '' : String(raw);
}

function asText(value: any): string {
  return value === null || value === undefined ? '' : String(value);
}

// One comparison read back off a stored rule. The same three fields EdgeComparison has, plus the one
// the `if` editor has no control for: `valuePath` is set when the right-hand side is itself a field
// ({ var: 'output.threshold' }), and '' when it is a literal. A repeat's `where` can author that shape,
// so the PARSER understands it and each caller decides whether it can show it.
export interface RuleComparison extends EdgeComparison { valuePath: string; }

// A single JSON-logic comparison -> its parts, or null when it is a shape no editor can represent (a
// nested and/or, an unknown operator, a comparison whose left side is not a field). The one reader for
// the operator table, so buildComparison and this cannot disagree about arg order.
export function parseComparison(rule: any): RuleComparison | null {
  if (!rule || typeof rule !== 'object' || Array.isArray(rule)) { return null; }
  const keys = Object.keys(rule);
  if (keys.length !== 1) { return null; }
  const op = keys[0];
  const args = rule[op];
  if (!Array.isArray(args)) { return null; }

  if ((op === '!!' || op === '!') && args.length === 1) {
    const field = varPath(args[0]);
    if (field === null) { return null; }
    return { field, operator: op === '!!' ? 'exists' : 'empty', value: '', valuePath: '' };
  }
  // An OR of equalities on ONE field is how this editor stores a membership check - see orOfEquals.
  if (op === 'or') { return parseOneOf(args); }
  if (op === 'in' && args.length === 2) {
    // A literal ARRAY on the right is the membership form, whose field is the FIRST arg. Tested before
    // the substring form because the two share the operator and only the right-hand side tells them
    // apart - read the other way round, "email is one of [...]" would come back as "[...] contains email".
    //
    // READ ONLY: orOfEquals no longer writes this shape, because Firestore refuses an array inside an
    // array. It is still read, because a rule hand-written straight into the document (or written by
    // another client) runs perfectly well - the restriction is on storing it from here, not on the rule.
    if (Array.isArray(args[1])) {
      const field = varPath(args[0]);
      // An entry that is not a primitive is refused rather than shown: it would read back as the text
      // "[object Object]", which a re-save then writes into the rule - the same trap parseIfRule refuses
      // for a field-vs-field comparison. Refusing keeps the rule verbatim instead.
      if (field === null || args[1].some((v: any) => v !== null && typeof v === 'object')) { return null; }
      return { field, operator: 'oneof', value: joinListValue(args[1]), valuePath: '' };
    }
    const field = varPath(args[1]);
    if (field === null) { return null; }        // membership over a non-field is not editable here
    return { field, operator: 'contains', ...rhs(args[0]) };
  }
  const mapped = JSONLOGIC_TO_OPERATOR[op];
  if (mapped && args.length === 2) {
    const field = varPath(args[0]);
    if (field === null) { return null; }
    return { field, operator: mapped, ...rhs(args[1]) };
  }
  return null;
}

// An `or` read back as a membership check, or null when it is a genuine or-join of different
// conditions. It claims the rule only when EVERY clause is an equality, against a literal, on the SAME
// field - which is precisely what orOfEquals writes and what an or-join of different conditions never
// is. Anything else falls through, and workflow-filter's join reader takes it instead.
//
// A user who authors two "equals" rows on one field joined with "or" stores this same shape and so
// reopens as one "is one of" row. The check is identical; only the name it is offered under changes.
function parseOneOf(args: any): RuleComparison | null {
  if (!Array.isArray(args) || args.length < 2) { return null; }
  let field: string | null = null;
  const values: any[] = [];
  for (const clause of args) {
    const parsed = parseComparison(clause);
    if (!parsed || parsed.operator !== 'eq' || parsed.valuePath) { return null; }
    if (field === null) { field = parsed.field; } else if (parsed.field !== field) { return null; }
    values.push(parsed.value);
  }
  return field === null ? null : { field, operator: 'oneof', value: joinListValue(values), valuePath: '' };
}

// The right-hand operand as the draft carries it: a field reference lands in `valuePath`, anything else
// is a literal in `value`. Kept apart because they are stored differently - a literal is coerced, a
// path is re-emitted as a `var` node.
function rhs(node: any): { value: string; valuePath: string } {
  const path = varPath(node);
  return path === null ? { value: asText(node), valuePath: '' } : { value: '', valuePath: path };
}

// A stored `if` rule -> the comparison the editor shows, or null when it is a shape the editor cannot
// represent (a nested and/or, an unknown operator, a comparison of two fields). Null is not an error:
// the rule still runs, the editor just declines to rewrite it.
export function parseIfRule(rule: any): EdgeComparison | null {
  const parsed = parseComparison(rule);
  // A field-vs-FIELD comparison is refused rather than shown: the `if` builder has no control for a
  // path on the right, and before parseComparison existed this read back as the literal text
  // "[object Object]" - which a re-save then wrote into the rule, silently breaking the branch.
  if (!parsed || parsed.valuePath) { return null; }
  return { field: parsed.field, operator: parsed.operator, value: parsed.value };
}

// The condition on a connection, in one short phrase for the arrow label and the editor: '' for a
// plain arrow, 'else' for the fallback, otherwise 'if <field> <check> <value>'. A rule the editor
// cannot read back is shown as 'if (custom rule)' rather than hidden, so it never reads as plain.
export function describeCondition(conn: { if?: any; else?: boolean } | null | undefined): string {
  if (!conn) { return ''; }
  if (conn.else === true) { return 'else'; }
  if (conn.if === undefined || conn.if === null) { return ''; }
  const parsed = parseIfRule(conn.if);
  if (!parsed) { return 'if (custom rule)'; }
  const short = WORKFLOW_OPERATORS.find(o => o.value === parsed.operator)?.short || parsed.operator;
  // Every no-value operator ends here, not just `exists`: its short label IS the whole phrase ("is set",
  // "not set"), and appending an empty value would leave a trailing space on the arrow label.
  if (!operatorTakesValue(parsed.operator)) { return `if ${parsed.field} ${short}`; }
  return `if ${parsed.field} ${short} ${parsed.value}`;
}

// The GATE phrase drawn on an arrow, taking primitives so this module stays free of the canvas types.
// A lone plain arrow returns '' - it has nothing to say, and it drew no label today either; only a FORK
// still prompts for a rule. Lifted out of the canvas template so it is testable, and so an arrow that
// only REPEATS can draw its own pill without this one reading "+ set a rule" beside it.
export function edgeGateLabel(state: 'if' | 'else' | 'plain', cond: string, fork: boolean): string {
  if (state === 'else') { return 'everything else'; }
  if (state === 'if') { return cond; }
  return fork ? '+ set a rule' : '';
}

// Why a comparison cannot be saved, or null when it can. One answer, so the Save button and the error
// line cannot disagree - the same discipline validateTriggerDraft uses.
export function conditionValidationError(field: string, operator: string, value: string): string | null {
  if (!(field || '').trim()) { return 'Choose a field to check, for example output.category.'; }
  if (!operatorTakesValue(operator)) { return null; }
  // A list needs at least one REAL entry: "  ,  " is non-blank, so the plain check below would pass it
  // and save an empty array - a rule that matches nothing, silently.
  if (operatorTakesList(operator)) {
    return splitListValue(value).length ? null : 'Enter at least one value, separated by commas.';
  }
  if (!(value ?? '').toString().trim()) { return 'Enter a value to compare against.'; }
  if (NUMERIC_OPERATORS.includes(operator) && isNaN(Number(value))) {
    return 'This check needs a number.';
  }
  return null;
}

// The live, plain-language echo of a comparison as the editor builds it: "<field> <operator label>
// <value>". Deliberately the operator's WORD label (equals, is at least) rather than describeCondition's
// symbol (=, ≥), so the preview reads as a business sentence, not the compact arrow label. '' until a
// field is entered; the value shows as '…' until typed, and is dropped entirely for a no-value operator.
export function rulePreview(field: string, operator: string, value: string): string {
  const f = (field || '').trim();
  if (!f) { return ''; }
  const label = WORKFLOW_OPERATORS.find(o => o.value === operator)?.label || operator;
  const tail = operatorTakesValue(operator) ? ' ' + ((value || '').trim() || '…') : '';
  return f + ' ' + label + tail;
}

// Apply a field helper chip to the current field text. A bare prefix ('output.') keeps what the user
// already typed - so their key survives and they only think about the business word: it is prepended
// when missing, and left untouched when the field already starts with it (clicking it again is a
// no-op, never a wipe). A full example ('output.category') replaces the box outright.
export function applyFieldChip(current: string, chip: string): string {
  if (chip.endsWith('.')) {
    if (!current) { return chip; }
    return current.startsWith(chip) ? current : chip + current;
  }
  return chip;
}

// --- Several comparisons as ONE rule ------------------------------------------------------------
//
// An arrow asks a single question, so buildIfRule / parseIfRule are enough for it. A document
// trigger's "Only when" is a LIST - every line has to hold for the trigger to fire - and that is a
// top-level `and` in the same dialect. Kept here beside the single-comparison pair so both forms
// share one operator table and one arg order.

// The rules -> the `if` as it should be stored. Null for none, the rule ITSELF for exactly one (an
// `and` wrapping a single term is noise the runner would have to unwrap), and `{and: [...]}` beyond
// that.
export function buildAllRule(rules: any[]): any {
  const parts = (rules || []).filter(r => r !== null && r !== undefined);
  if (!parts.length) { return null; }
  if (parts.length === 1) { return parts[0]; }
  return { and: parts };
}

// A stored rule -> the comparisons a list-shaped editor can show, or null when it is a shape the
// form cannot render and must therefore keep verbatim.
//
// Three cases: a bare comparison is a one-line list, a top-level `and` is its terms, and anything
// else - an `or`, a nested `and`, a term that is not a plain comparison - is refused. Refusing is
// not an error: the rule still runs, the editor just declines to rewrite what it cannot read.
export function parseAllRule(rule: any): EdgeComparison[] | null {
  if (rule === null || rule === undefined) { return []; }
  if (rule && typeof rule === 'object' && !Array.isArray(rule) && Object.keys(rule).length === 1
      && Array.isArray((rule as any).and)) {
    const terms = (rule as any).and;
    // An `and` of nothing is not a list anyone typed here; treat it as unreadable rather than as an
    // empty condition, so a save cannot quietly drop it.
    if (!terms.length) { return null; }
    const out: EdgeComparison[] = [];
    for (const term of terms) {
      const parsed = parseIfRule(term);
      if (!parsed) { return null; }
      out.push(parsed);
    }
    return out;
  }
  const single = parseIfRule(rule);
  return single ? [single] : null;
}
